import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, inr, fmtDate } from "@/lib/api";
import { PageHeader, StatCard } from "@/components/app/UI";
import RowDeleteButton from "@/components/app/RowDeleteButton";
import Pagination from "@/components/app/Pagination";
import SearchablePicker, { projectMatch, renderProjectRow } from "@/components/app/SearchablePicker";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Plus, Search, History, Wallet, ListChecks, FileBarChart, Banknote, AlertTriangle, Download, FileSpreadsheet, File as FileIcon } from "lucide-react";
import { toast } from "sonner";

// --- Date range presets ---------------------------------------------------
const ymd = (d) => {
  const x = new Date(d);
  return `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, "0")}-${String(x.getDate()).padStart(2, "0")}`;
};
const rangePresets = () => {
  const now = new Date();
  const today = ymd(now);
  const weekStart = new Date(now); weekStart.setDate(now.getDate() - now.getDay());
  const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
  const lastMonthEnd = new Date(now.getFullYear(), now.getMonth(), 0);
  const lastMonthStart = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  return {
    today:      { from: today, to: today },
    week:       { from: ymd(weekStart), to: today },
    month:      { from: ymd(monthStart), to: today },
    last_month: { from: ymd(lastMonthStart), to: ymd(lastMonthEnd) },
  };
};

const STAGES = ["Order Confirmation", "Documentation", "Installation", "Commissioning", "Closure",
                "Order Confirmed", "Technical Assessment", "Material Procurement", "Site Survey",
                "Initial Payment", "Compliance"];

export default function Finance() {
  const [searchParams] = useSearchParams();
  const initialTab = searchParams.get("tab") || "pending";
  const initialProjectId = searchParams.get("project_id") || "";
  const initialExpenseTypes = searchParams.get("expense_types") || "";
  const initialQ = searchParams.get("q") || "";

  const [tab, setTab] = useState(initialTab);
  const [open, setOpen] = useState(false);
  const [auditOpen, setAuditOpen] = useState(false);
  // Filters shared across all tabs
  const [preset, setPreset] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [q, setQ] = useState(initialQ);
  const [stage, setStage] = useState("all");
  const [status, setStatus] = useState("all");
  // Deep-link only — narrows project + expense buckets when arriving from a card link
  const [projectIdFilter, setProjectIdFilter] = useState(initialProjectId);
  const [expenseTypesFilter, setExpenseTypesFilter] = useState(initialExpenseTypes);

  const applyPreset = (key) => {
    setPreset(key);
    if (key === "all" || key === "custom") return;
    const r = rangePresets()[key];
    setDateFrom(r.from); setDateTo(r.to);
  };

  const params = useMemo(() => {
    const p = {};
    if (dateFrom) p.date_from = dateFrom;
    if (dateTo) p.date_to = dateTo;
    if (q) p.q = q;
    if (stage !== "all") p.stage = stage;
    if (status !== "all") p.status = status;
    if (projectIdFilter) p.project_id = projectIdFilter;
    if (expenseTypesFilter) p.expense_types = expenseTypesFilter;
    return p;
  }, [dateFrom, dateTo, q, stage, status, projectIdFilter, expenseTypesFilter]);

  const deepLinkActive = !!(projectIdFilter || expenseTypesFilter);
  const clearDeepLink = () => { setProjectIdFilter(""); setExpenseTypesFilter(""); };

  return (
    <div data-testid="finance-page">
      <PageHeader
        eyebrow="Finance Module"
        title="Financial Management"
        subtitle="Collections, expenses, dashboard and audit trail — all filterable, all linked to your projects."
        action={
          <div className="flex gap-2">
            <ExportMenu params={params} kind={tab} />
            <Button onClick={() => setAuditOpen(true)} variant="outline" className="rounded-none border-[#0a0a0a] uppercase tracking-widest text-xs font-bold h-11" data-testid="finance-audit-btn">
              <History className="w-4 h-4 mr-2" /> Audit Trail
            </Button>
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button className="rounded-none bg-[#002FA7] hover:bg-[#002480] uppercase tracking-widest text-xs font-bold h-11 px-6" data-testid="new-payment-button"><Plus className="w-4 h-4 mr-2" /> New Transaction</Button>
              </DialogTrigger>
              {open && <PaymentDialog onDone={() => { setOpen(false); window.dispatchEvent(new Event("finance:refresh")); }} />}
            </Dialog>
          </div>
        }
      />

      <div className="p-4 sm:p-8 space-y-5">
        {/* FILTERS BAR */}
        <div className="border border-[#E5E7EB] bg-white p-3 flex flex-wrap items-center gap-2">
          <Select value={preset} onValueChange={applyPreset}>
            <SelectTrigger className="rounded-none h-9 w-[160px]" data-testid="finance-preset"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All time</SelectItem>
              <SelectItem value="today">Today</SelectItem>
              <SelectItem value="week">This Week</SelectItem>
              <SelectItem value="month">This Month</SelectItem>
              <SelectItem value="last_month">Last Month</SelectItem>
              <SelectItem value="custom">Custom…</SelectItem>
            </SelectContent>
          </Select>
          {(preset === "custom" || preset === "all") && (
            <>
              <Input type="date" value={dateFrom} onChange={(e) => { setDateFrom(e.target.value); setPreset("custom"); }} className="rounded-none h-9 w-[140px]" data-testid="finance-from" placeholder="From" />
              <Input type="date" value={dateTo} onChange={(e) => { setDateTo(e.target.value); setPreset("custom"); }} className="rounded-none h-9 w-[140px]" data-testid="finance-to" placeholder="To" />
            </>
          )}
          <div className="relative flex-1 min-w-[200px]">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA3AF]" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search customer / phone / project ID / note…" className="pl-9 h-9 rounded-none" data-testid="finance-search" />
          </div>
          <Select value={stage} onValueChange={setStage}>
            <SelectTrigger className="rounded-none h-9 w-[180px]" data-testid="finance-stage"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Stages</SelectItem>
              {STAGES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="rounded-none h-9 w-[160px]" data-testid="finance-status"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value="Fully Paid">Fully Paid</SelectItem>
              <SelectItem value="Partially Paid">Partially Paid</SelectItem>
              <SelectItem value="Pending">Pending</SelectItem>
            </SelectContent>
          </Select>
          {(dateFrom || dateTo || q || stage !== "all" || status !== "all") && (
            <Button onClick={() => { setPreset("all"); setDateFrom(""); setDateTo(""); setQ(""); setStage("all"); setStatus("all"); }} variant="ghost" size="sm" className="rounded-none text-xs uppercase tracking-widest font-bold" data-testid="finance-clear">Clear</Button>
          )}
        </div>

        {deepLinkActive && (
          <div className="border border-[#002FA7] bg-[#EFF6FF] p-3 flex flex-wrap items-center gap-2 text-xs" data-testid="finance-deeplink-banner">
            <span className="label-eyebrow !text-[10px] text-[#002FA7]">Filtered View</span>
            {projectIdFilter && <span className="font-mono">project:{projectIdFilter.slice(0, 8)}…</span>}
            {expenseTypesFilter && <span>expense types: <b>{expenseTypesFilter}</b></span>}
            <Button onClick={clearDeepLink} size="sm" variant="ghost" className="ml-auto rounded-none text-xs uppercase tracking-widest font-bold text-[#002FA7]" data-testid="finance-clear-deeplink">
              Clear deep-link
            </Button>
          </div>
        )}

        {/* TABS */}
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="rounded-none border border-[#E5E7EB] bg-white p-0 flex-wrap h-auto">
            <TabsTrigger value="pending" className="rounded-none data-[state=active]:bg-[#0a0a0a] data-[state=active]:text-white uppercase tracking-widest text-xs font-bold px-5 h-10 flex items-center gap-1.5" data-testid="fin-tab-pending"><ListChecks className="w-3.5 h-3.5" /> Pending Collections</TabsTrigger>
            <TabsTrigger value="buckets" className="rounded-none data-[state=active]:bg-[#0a0a0a] data-[state=active]:text-white uppercase tracking-widest text-xs font-bold px-5 h-10 flex items-center gap-1.5" data-testid="fin-tab-buckets"><AlertTriangle className="w-3.5 h-3.5" /> Collection Buckets</TabsTrigger>
            <TabsTrigger value="confirm" className="rounded-none data-[state=active]:bg-[#0a0a0a] data-[state=active]:text-white uppercase tracking-widest text-xs font-bold px-5 h-10 flex items-center gap-1.5" data-testid="fin-tab-confirm"><ListChecks className="w-3.5 h-3.5" /> Pending for Confirmation</TabsTrigger>
            <TabsTrigger value="collections" className="rounded-none data-[state=active]:bg-[#0a0a0a] data-[state=active]:text-white uppercase tracking-widest text-xs font-bold px-5 h-10 flex items-center gap-1.5" data-testid="fin-tab-collections"><Banknote className="w-3.5 h-3.5" /> Collections Received</TabsTrigger>
            <TabsTrigger value="expenses" className="rounded-none data-[state=active]:bg-[#0a0a0a] data-[state=active]:text-white uppercase tracking-widest text-xs font-bold px-5 h-10 flex items-center gap-1.5" data-testid="fin-tab-expenses"><Wallet className="w-3.5 h-3.5" /> Expenses</TabsTrigger>
            <TabsTrigger value="dashboard" className="rounded-none data-[state=active]:bg-[#0a0a0a] data-[state=active]:text-white uppercase tracking-widest text-xs font-bold px-5 h-10 flex items-center gap-1.5" data-testid="fin-tab-dashboard"><FileBarChart className="w-3.5 h-3.5" /> Dashboard</TabsTrigger>
          </TabsList>
        </Tabs>

        {tab === "pending"     && <PendingTab params={params} />}
        {tab === "buckets"     && <BucketsTab params={params} />}
        {tab === "confirm"     && <PendingConfirmTab params={params} />}
        {tab === "collections" && <TxTab params={{ ...params, transaction_type: "Project", kind: "Customer" }} testidPrefix="coll" emptyMsg="No customer collections found." />}
        {tab === "expenses"    && <ExpensesTab params={params} />}
        {tab === "dashboard"   && <DashboardTab params={params} />}

        <AuditDialog open={auditOpen} onOpenChange={setAuditOpen} />
      </div>
    </div>
  );
}

// ------------------------------------------------------------------ Pending Collections tab
function PendingTab({ params }) {
  const [data, setData] = useState({ rows: [], totals: {} });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const reload = () => api.get("/finance/pending-collections", { params }).then((r) => setData(r.data));
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [JSON.stringify(params)]);
  useEffect(() => { const h = () => reload(); window.addEventListener("finance:refresh", h); return () => window.removeEventListener("finance:refresh", h); });
  useEffect(() => { setPage(1); }, [JSON.stringify(params), pageSize]);
  const paged = useMemo(() => {
    const totalPages = Math.max(1, Math.ceil((data.rows || []).length / pageSize));
    const safe = Math.min(page, totalPages);
    return (data.rows || []).slice((safe - 1) * pageSize, safe * pageSize);
  }, [data.rows, page, pageSize]);

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard testid="pending-count" label="Projects" value={data.totals.count ?? 0} />
        <StatCard testid="pending-contract" label="Total Contract" value={inr(data.totals.contract_value || 0)} />
        <StatCard testid="pending-collected" label="Total Collected" value={inr(data.totals.collected || 0)} accent="success" />
        <StatCard testid="pending-pending" label="Total Pending" value={inr(data.totals.pending || 0)} accent="warning" />
      </div>
      <Table testidEmpty="pending-empty" headers={["Customer", "Project ID", "Contract", "Collected", "Pending", "Status", "Stage", "Last Payment"]}>
        {paged.map((r) => (
          <tr key={r.project_id} className="border-b border-[#F3F4F6] hover:bg-[#FAFAFA]" data-testid={`pending-row-${r.project_id}`}>
            <td className="px-4 py-3"><Link to={`/projects/${r.project_id}`} className="font-bold hover:underline">{r.customer_name}</Link></td>
            <td className="px-4 py-3 text-xs label-eyebrow !text-[10px]">{r.project_qr || "—"}</td>
            <td className="px-4 py-3 text-xs font-semibold">{inr(r.contract_value)}</td>
            <td className="px-4 py-3 text-xs font-semibold text-[#10B981]">{inr(r.collected)}</td>
            <td className="px-4 py-3 text-xs font-bold text-[#DC2626]">{inr(r.pending)}</td>
            <td className="px-4 py-3"><Chip status={r.payment_status} /></td>
            <td className="px-4 py-3 text-xs">{r.current_stage || "—"}</td>
            <td className="px-4 py-3 text-xs text-[#4B5563]">{r.last_payment_date ? fmtDate(r.last_payment_date) : "—"}</td>
          </tr>
        ))}
      </Table>
      <Pagination
        total={(data.rows || []).length}
        page={page}
        pageSize={pageSize}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
        testidPrefix="pending-pagination"
      />
    </div>
  );
}

// ------------------------------------------------------------------ Generic transactions tab (Collections, Expenses)
function TxTab({ params, testidPrefix, emptyMsg }) {
  const [data, setData] = useState({ transactions: [], summary: {} });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const reload = () => api.get("/finance/transactions", { params }).then((r) => setData(r.data));
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [JSON.stringify(params)]);
  useEffect(() => { const h = () => reload(); window.addEventListener("finance:refresh", h); return () => window.removeEventListener("finance:refresh", h); });
  useEffect(() => { setPage(1); }, [JSON.stringify(params), pageSize]);

  const paged = useMemo(() => {
    const total = data.transactions.length;
    const totalPages = Math.max(1, Math.ceil(total / pageSize));
    const safe = Math.min(page, totalPages);
    return data.transactions.slice((safe - 1) * pageSize, safe * pageSize);
  }, [data.transactions, page, pageSize]);

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard testid={`${testidPrefix}-count`} label="Transactions" value={data.summary.count ?? 0} />
        <StatCard testid={`${testidPrefix}-collected`} label="Collected" value={inr(data.summary.collected || 0)} accent="success" />
        <StatCard testid={`${testidPrefix}-pending`} label="Pending Invoices" value={inr(data.summary.pending_invoices || 0)} accent="warning" />
        <StatCard testid={`${testidPrefix}-net`} label="Net (Coll − Exp)" value={inr(data.summary.net_cash_flow || 0)} accent="primary" />
      </div>
      <Table testidEmpty={`${testidPrefix}-empty`} emptyMsg={emptyMsg} headers={["Date", "Customer", "Project ID", "Stage / Type", "Amount", "Mode", "Status", "Notes", ""]}>
        {paged.map((t) => (
          <TxRow key={t.id} t={t} onChanged={reload} />
        ))}
      </Table>
      <Pagination
        total={data.transactions.length}
        page={page}
        pageSize={pageSize}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
        testidPrefix={`${testidPrefix}-pagination`}
      />
    </div>
  );
}

function ExpensesTab({ params }) {
  // Both Project Vendor + Non-Project together
  const [data, setData] = useState({ transactions: [], summary: {} });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const reload = () => api.get("/finance/transactions", { params: { ...params, kind: "Vendor" } }).then((r) => setData(r.data));
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [JSON.stringify(params)]);
  useEffect(() => { const h = () => reload(); window.addEventListener("finance:refresh", h); return () => window.removeEventListener("finance:refresh", h); });
  useEffect(() => { setPage(1); }, [JSON.stringify(params), pageSize]);

  const total = data.transactions.reduce((s, t) => s + (t.amount_inr || 0), 0);
  const paged = useMemo(() => {
    const totalPages = Math.max(1, Math.ceil(data.transactions.length / pageSize));
    const safe = Math.min(page, totalPages);
    return data.transactions.slice((safe - 1) * pageSize, safe * pageSize);
  }, [data.transactions, page, pageSize]);

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <StatCard testid="exp-count" label="Expense Entries" value={data.summary.count ?? 0} />
        <StatCard testid="exp-total" label="Total Expenses" value={inr(total)} accent="warning" />
        <StatCard testid="exp-net" label="Net (Coll − Exp)" value={inr(data.summary.net_cash_flow || 0)} accent="primary" />
      </div>
      <Table testidEmpty="exp-empty" emptyMsg="No expenses found." headers={["Date", "Project / Type", "Expense Type", "Category", "Head / Note", "Amount", "Mode", ""]}>
        {paged.map((t) => (
          <tr key={t.id} className="border-b border-[#F3F4F6] hover:bg-[#FAFAFA]" data-testid={`exp-row-${t.id}`}>
            <td className="px-4 py-3 text-xs">{fmtDate(t.tx_date || t.created_at)}</td>
            <td className="px-4 py-3 text-xs">
              {t.transaction_type === "Non-Project Expense" ? (
                <span className="px-2 py-0.5 bg-[#FEF3C7] text-[#92400E] text-[10px] uppercase tracking-widest font-bold">Non-Project</span>
              ) : (
                <Link to={`/projects/${t.project_id}`} className="font-bold hover:underline">{t.customer_name || "—"}</Link>
              )}
            </td>
            <td className="px-4 py-3 text-xs font-bold">{t.expense_type || "—"}</td>
            <td className="px-4 py-3 text-xs">{t.category || "—"}</td>
            <td className="px-4 py-3 text-xs">{t.milestone || t.note || "—"}</td>
            <td className="px-4 py-3 text-xs font-bold text-[#DC2626]">{inr(t.amount_inr)}</td>
            <td className="px-4 py-3 text-xs">{t.method}</td>
            <td className="px-4 py-3 text-right"><RowDeleteButton endpoint={`/payments/${t.id}`} confirmMessage={`Delete this expense of ${inr(t.amount_inr)}?`} onDeleted={reload} testid={`exp-delete-${t.id}`} successMessage="Expense deleted" /></td>
          </tr>
        ))}
      </Table>
      <Pagination
        total={data.transactions.length}
        page={page}
        pageSize={pageSize}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
        testidPrefix="exp-pagination"
      />
    </div>
  );
}

function TxRow({ t, onChanged }) {
  return (
    <tr className="border-b border-[#F3F4F6] hover:bg-[#FAFAFA]" data-testid={`tx-row-${t.id}`}>
      <td className="px-4 py-3 text-xs">{fmtDate(t.tx_date || t.created_at)}</td>
      <td className="px-4 py-3 text-xs">{t.project_id ? <Link to={`/projects/${t.project_id}`} className="font-bold hover:underline">{t.customer_name || "—"}</Link> : "—"}</td>
      <td className="px-4 py-3 text-xs label-eyebrow !text-[10px]">{t.project_qr || "—"}</td>
      <td className="px-4 py-3 text-xs">{t.milestone || t.category || "—"}</td>
      <td className="px-4 py-3 text-xs font-bold text-[#10B981]">{inr(t.amount_inr)}</td>
      <td className="px-4 py-3 text-xs">{t.method}</td>
      <td className="px-4 py-3"><Chip status={t.status} /></td>
      <td className="px-4 py-3 text-xs text-[#4B5563]">{t.note || "—"}</td>
      <td className="px-4 py-3 text-right"><RowDeleteButton endpoint={`/payments/${t.id}`} confirmMessage={`Delete this transaction of ${inr(t.amount_inr)}?`} onDeleted={onChanged} testid={`tx-delete-${t.id}`} successMessage="Transaction deleted" /></td>
    </tr>
  );
}

// ------------------------------------------------------------------ Pending for Confirmation tab
function PendingConfirmTab({ params }) {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [confirming, setConfirming] = useState(null); // payment being confirmed via dialog

  const reload = () => {
    const p = new URLSearchParams({ paged: "1", limit: "200", kind: "Customer", status: "Pending Confirmation" });
    if (params?.q) p.set("q", params.q);
    if (params?.date_from) p.set("date_from", params.date_from);
    if (params?.date_to) p.set("date_to", params.date_to);
    setLoading(true);
    api.get(`/payments?${p.toString()}`)
      .then((r) => { setRows(r.data.items || []); setTotal(r.data.total || 0); })
      .finally(() => setLoading(false));
  };
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [JSON.stringify(params)]);
  useEffect(() => { const h = () => reload(); window.addEventListener("finance:refresh", h); return () => window.removeEventListener("finance:refresh", h); });

  const quickConfirm = async (p) => {
    try {
      await api.post(`/payments/${p.id}/confirm`);
      toast.success(`Confirmed ${inr(p.amount_inr)} from ${p.customer_name || "Non-project"}`);
      reload();
      window.dispatchEvent(new Event("finance:refresh"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to confirm");
    }
  };

  const quickReject = async (p) => {
    const reason = window.prompt(
      `Reject ${inr(p.amount_inr)} from ${p.customer_name || "this entry"}? It will leave the queue but stay in the audit trail.\n\nReason (optional):`,
      "",
    );
    if (reason === null) return;
    try {
      await api.post(`/payments/${p.id}/reject`, reason.trim() ? { note: reason.trim() } : {});
      toast.success("Entry rejected");
      reload();
      window.dispatchEvent(new Event("finance:refresh"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to reject");
    }
  };

  const totalAmount = rows.reduce((s, r) => s + (Number(r.amount_inr) || 0), 0);

  return (
    <div className="space-y-4 mt-6" data-testid="fin-confirm-tab">
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flat-card px-4 py-3">
          <div className="label-eyebrow !text-[10px]">Awaiting Confirmation</div>
          <div className="text-xl font-black mt-1" data-testid="fin-confirm-count">{total}</div>
        </div>
        <div className="flat-card px-4 py-3">
          <div className="label-eyebrow !text-[10px]">Total to Confirm</div>
          <div className="text-xl font-black mt-1 text-[#F59E0B]" data-testid="fin-confirm-amount">{inr(totalAmount)}</div>
        </div>
        <div className="text-xs text-[#4B5563] ml-auto">
          New customer transactions land here until an admin / finance user verifies the money has hit the bank or cash counter.
        </div>
      </div>

      <Table
        headers={["Date", "Customer", "QR", "Stage", "Amount", "Method", "Recorded By", "Remarks", ""]}
        emptyMsg={loading ? "Loading…" : "Nothing awaiting confirmation — new customer transactions will show up here."}
        testidEmpty="fin-confirm-empty"
      >
        {rows.map((t) => (
          <tr key={t.id} className="border-b border-[#F3F4F6] hover:bg-[#FFFBEB]" data-testid={`confirm-row-${t.id}`}>
            <td className="px-4 py-3 text-xs">{fmtDate(t.tx_date || t.created_at)}</td>
            <td className="px-4 py-3 text-xs">{t.project_id ? <Link to={`/projects/${t.project_id}`} className="font-bold hover:underline">{t.customer_name || "—"}</Link> : "—"}</td>
            <td className="px-4 py-3 text-xs label-eyebrow !text-[10px]">{t.project_qr || "—"}</td>
            <td className="px-4 py-3 text-xs">{t.milestone || "—"}</td>
            <td className="px-4 py-3 text-xs font-bold text-[#F59E0B]">{inr(t.amount_inr)}</td>
            <td className="px-4 py-3 text-xs">{t.method}{t.reference && <div className="text-[10px] text-[#9CA3AF]">Ref: {t.reference}</div>}</td>
            <td className="px-4 py-3 text-xs text-[#4B5563]">{t.recorded_by || "—"}</td>
            <td className="px-4 py-3 text-xs text-[#4B5563]">{t.note || "—"}</td>
            <td className="px-4 py-3 text-right whitespace-nowrap">
              <div className="inline-flex gap-1.5">
                <Button
                  size="sm"
                  onClick={() => quickConfirm(t)}
                  className="rounded-none h-7 px-2 text-[10px] uppercase tracking-widest font-bold bg-[#10B981] hover:bg-[#059669] text-white"
                  data-testid={`confirm-row-btn-${t.id}`}
                >
                  Confirm Received
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setConfirming(t)}
                  className="rounded-none h-7 px-2 text-[10px] uppercase tracking-widest font-bold text-[#002FA7]"
                  data-testid={`confirm-row-edit-${t.id}`}
                >
                  Edit & Confirm
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => quickReject(t)}
                  className="rounded-none h-7 px-2 text-[10px] uppercase tracking-widest font-bold text-[#DC2626] hover:bg-[#FEE2E2]"
                  data-testid={`confirm-row-reject-${t.id}`}
                >
                  Reject
                </Button>
              </div>
            </td>
          </tr>
        ))}
      </Table>

      <ConfirmPaymentDialog
        payment={confirming}
        onClose={() => setConfirming(null)}
        onDone={() => { setConfirming(null); reload(); window.dispatchEvent(new Event("finance:refresh")); }}
      />
    </div>
  );
}

function ConfirmPaymentDialog({ payment, onClose, onDone }) {
  const [form, setForm] = useState({ amount_inr: "", method: "", reference: "", tx_date: "", note: "" });
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!payment) return;
    setForm({
      amount_inr: payment.amount_inr || "",
      method: payment.method || "UPI",
      reference: payment.reference || "",
      tx_date: payment.tx_date || new Date().toISOString().slice(0, 10),
      note: payment.note || "",
    });
  }, [payment]);
  if (!payment) return null;
  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/payments/${payment.id}/confirm`, {
        amount_inr: Number(form.amount_inr),
        method: form.method,
        reference: form.reference || null,
        tx_date: form.tx_date || null,
        note: form.note || null,
      });
      toast.success("Payment confirmed");
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to confirm");
    } finally { setBusy(false); }
  };
  return (
    <Dialog open={!!payment} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-none max-w-md border-[#0a0a0a]" data-testid="confirm-payment-dialog">
        <DialogHeader><DialogTitle className="font-black text-xl tracking-tight">Confirm Received</DialogTitle></DialogHeader>
        <div className="text-xs text-[#4B5563] pb-2">
          <b>{payment.customer_name}</b> · {payment.milestone}
        </div>
        <div className="space-y-3">
          <div>
            <Label className="label-eyebrow">Amount Actually Received (INR)</Label>
            <Input type="number" value={form.amount_inr} onChange={(e) => setForm({ ...form, amount_inr: e.target.value })} className="rounded-none mt-1 h-10" data-testid="confirm-amount" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="label-eyebrow">Method</Label>
              <Select value={form.method} onValueChange={(v) => setForm({ ...form, method: v })}>
                <SelectTrigger className="rounded-none mt-1 h-10" data-testid="confirm-method"><SelectValue /></SelectTrigger>
                <SelectContent>{["UPI", "Cash", "Cheque", "Bank Transfer", "Card", "NEFT/IMPS"].map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="label-eyebrow">Date Credited</Label>
              <Input type="date" value={form.tx_date} onChange={(e) => setForm({ ...form, tx_date: e.target.value })} className="rounded-none mt-1 h-10" data-testid="confirm-date" />
            </div>
          </div>
          <div>
            <Label className="label-eyebrow">Reference / UTR</Label>
            <Input value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} className="rounded-none mt-1 h-10" data-testid="confirm-reference" />
          </div>
          <div>
            <Label className="label-eyebrow">Remarks</Label>
            <Input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} className="rounded-none mt-1 h-10" data-testid="confirm-note" />
          </div>
          <Button onClick={submit} disabled={busy} className="w-full rounded-none bg-[#10B981] hover:bg-[#059669] h-11 uppercase tracking-widest text-xs font-bold" data-testid="confirm-submit">
            {busy ? "Confirming…" : "Confirm & Record Received"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}



// ------------------------------------------------------------------ Dashboard tab
function DashboardTab({ params }) {
  const [d, setD] = useState(null);
  useEffect(() => {
    api.get("/finance/dashboard", { params }).then((r) => setD(r.data));
    // eslint-disable-next-line
  }, [JSON.stringify(params)]);
  useEffect(() => { const h = () => api.get("/finance/dashboard", { params }).then((r) => setD(r.data)); window.addEventListener("finance:refresh", h); return () => window.removeEventListener("finance:refresh", h); });

  if (!d) return <div className="label-eyebrow p-6">Loading…</div>;
  return (
    <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
      <StatCard testid="dash-contract" label="Total Contract Value" value={inr(d.contract_value)} />
      <StatCard testid="dash-collected" label="Total Collections" value={inr(d.collected)} accent="success" />
      <StatCard testid="dash-pending" label="Total Pending Collections" value={inr(d.pending_project)} accent="warning" />
      <StatCard testid="dash-expenses" label="Total Expenses" value={inr(d.expenses)} accent="error" />
      <StatCard testid="dash-net" label="Net Cash Flow" value={inr(d.net_cash_flow)} accent="primary" sub="Collections − Expenses" />
    </div>
  );
}

// ------------------------------------------------------------------ Shared helpers
function Table({ headers, children, emptyMsg = "No records found.", testidEmpty }) {
  const empty = !children || (Array.isArray(children) && children.length === 0);
  return (
    <div className="border border-[#E5E7EB] bg-white overflow-x-auto">
      <table className="w-full text-sm" style={{ minWidth: 900 }}>
        <thead className="bg-[#F9FAFB] border-b border-[#E5E7EB]">
          <tr>{headers.map((h, i) => <th key={i} className="text-left label-eyebrow !text-[10px] px-4 py-3">{h}</th>)}</tr>
        </thead>
        <tbody>
          {empty ? <tr><td colSpan={headers.length} className="text-center text-sm text-[#9CA3AF] py-10" data-testid={testidEmpty}>{emptyMsg}</td></tr> : children}
        </tbody>
      </table>
    </div>
  );
}

function Chip({ status }) {
  const cls = status === "Received" || status === "Fully Paid" ? "bg-[#D1FAE5] text-[#065F46]"
              : status === "Partially Paid" ? "bg-[#FEF3C7] text-[#92400E]"
              : "bg-[#FECACA] text-[#991B1B]";
  return <span className={`text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 ${cls}`}>{status}</span>;
}

// --- Collection Buckets tab ----------------------------------------------
function ReceiveCollectionDialog({ row, onClose, onDone }) {
  const outstanding = Number(row?.amount_inr || 0);
  const due = Number(row?.amount_due_inr ?? row?.amount_inr ?? 0);
  const [amount, setAmount] = useState("");
  const [method, setMethod] = useState("UPI");
  const [reference, setReference] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (row) { setAmount(String(outstanding)); setMethod("UPI"); setReference(""); }
  }, [row?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!row) return null;

  const amt = Number(amount);
  const amtOk = /^\d+(\.\d{1,2})?$/.test(String(amount).trim()) && amt > 0 && amt <= outstanding + 0.005;
  const balance = amtOk ? Math.max(outstanding - amt, 0) : null;
  const isPartial = amtOk && amt < outstanding - 0.005;

  const submit = async () => {
    if (!amtOk) return;
    setBusy(true);
    try {
      const payload = {
        amount_inr: Math.round(amt * 100) / 100,
        method, reference: reference || null,
      };
      if (row.is_synthetic) payload.project_id = row.project_id;
      else payload.entry_id = row.id;
      const r = await api.post("/finance/collection-buckets/receive", payload);
      const d = r.data || {};
      if (d.fully_received) {
        toast.success(`Fully received ${inr(amt)} — entry closed`);
      } else if (d.balance_inr > 0) {
        const where = d.balance_moved_to ? "added to the earliest other pending entry" : "moved to a new pending entry";
        toast.success(`Received ${inr(amt)} — balance ${inr(d.balance_inr)} ${where}`);
      } else {
        toast.success(`Received ${inr(amt)}`);
      }
      window.dispatchEvent(new Event("finance:refresh"));
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to record receipt");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={!!row} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-none border-[#0a0a0a] max-w-md" data-testid="receive-collection-dialog">
        <DialogHeader><DialogTitle className="font-black text-xl tracking-tight">Record Receipt</DialogTitle></DialogHeader>
        <div className="space-y-3 pt-1">
          <div className="border border-[#E5E7EB] bg-[#F9FAFB] p-3 text-xs space-y-1">
            <div className="flex justify-between"><span className="label-eyebrow !text-[9px]">Customer</span><span className="font-bold" data-testid="receive-customer">{row.customer_name || "—"}</span></div>
            <div className="flex justify-between"><span className="label-eyebrow !text-[9px]">Collection Date</span><span data-testid="receive-date">{row.expected_collection_date ? fmtDate(row.expected_collection_date) : "No date"}</span></div>
            <div className="flex justify-between"><span className="label-eyebrow !text-[9px]">Total Amount Due</span><span className="font-bold" data-testid="receive-due">{inr(due)}</span></div>
            {Number(row.received_inr || 0) > 0 && (
              <div className="flex justify-between"><span className="label-eyebrow !text-[9px]">Already Received</span><span className="text-[#065F46]">{inr(row.received_inr)}</span></div>
            )}
          </div>
          <div>
            <Label className="label-eyebrow">Amount Received (₹)</Label>
            <Input
              type="number" min="0.01" step="0.01" value={amount}
              onChange={(e) => setAmount(e.target.value)}
              className={`rounded-none mt-1 h-10 ${amount && !amtOk ? "border-[#DC2626]" : ""}`}
              data-testid="receive-amount-input"
            />
            {amount && !amtOk && (
              <div className="text-[10px] text-[#DC2626] mt-1" data-testid="receive-amount-error">
                Enter an amount greater than ₹0 and up to {inr(outstanding)} (max 2 decimals).
              </div>
            )}
          </div>
          <div className="flex justify-between items-center border-t border-[#E5E7EB] pt-2">
            <span className="label-eyebrow !text-[9px]">Balance Amount</span>
            <span className={`text-lg font-black ${balance !== null && balance > 0 ? "text-[#DC2626]" : "text-[#065F46]"}`} data-testid="receive-balance">
              {balance === null ? "—" : inr(balance)}
            </span>
          </div>
          {isPartial && (
            <div className="text-[10px] text-[#92400E] bg-[#FEF3C7] px-2 py-1.5" data-testid="receive-partial-hint">
              Partial receipt — the balance will move to the earliest other pending entry for this customer (or a new pending entry if none exists).
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="label-eyebrow">Method</Label>
              <select value={method} onChange={(e) => setMethod(e.target.value)} className="w-full mt-1 h-10 px-3 border border-[#E5E7EB] text-sm rounded-none bg-white" data-testid="receive-method">
                {["UPI", "Cash", "Cheque", "Bank Transfer", "—"].map((m) => <option key={m}>{m}</option>)}
              </select>
            </div>
            <div>
              <Label className="label-eyebrow">Reference (optional)</Label>
              <Input value={reference} onChange={(e) => setReference(e.target.value)} className="rounded-none mt-1 h-10" data-testid="receive-reference" />
            </div>
          </div>
          <div className="flex gap-2 justify-end pt-1">
            <Button variant="outline" onClick={onClose} disabled={busy} className="rounded-none uppercase tracking-widest text-xs font-bold" data-testid="receive-cancel">Cancel</Button>
            <Button onClick={submit} disabled={busy || !amtOk} className="rounded-none bg-[#10B981] hover:bg-[#059669] uppercase tracking-widest text-xs font-bold disabled:bg-[#9CA3AF]" data-testid="receive-confirm">
              {busy ? "Recording…" : isPartial ? "Confirm Partial Receipt" : "Confirm Received"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function BucketRow({ r, onChanged, onReceive }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(r.expected_collection_date ? r.expected_collection_date.slice(0, 10) : "");
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (!val) { toast.error("Pick a date"); return; }
    setBusy(true);
    try {
      if (r.is_synthetic) {
        await api.post("/finance/collection-buckets/from-synthetic", {
          project_id: r.project_id, amount_inr: r.amount_inr, expected_collection_date: val,
        });
        toast.success("Pending payment created with expected date");
      } else {
        await api.patch(`/payments/${r.id}`, { expected_collection_date: val });
        toast.success("Expected date updated");
      }
      setEditing(false);
      onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  const due = Number(r.amount_due_inr ?? r.amount_inr ?? 0);
  const receivedSoFar = Number(r.received_inr || 0);
  const statusLabel = r.collection_status || "Pending";
  const statusCls = statusLabel === "Partially Received" ? "bg-[#FEF3C7] text-[#92400E]" : "bg-[#F3F4F6] text-[#4B5563]";

  return (
    <tr className={`border-b border-[#F3F4F6] hover:bg-[#FAFAFA] ${r.is_synthetic ? "bg-[#FAFAF7]" : ""}`} data-testid={`bucket-row-${r.id}`}>
      <td className="px-4 py-2 text-xs">
        {editing ? (
          <div className="flex items-center gap-1">
            <Input type="date" value={val} onChange={(e) => setVal(e.target.value)} className="rounded-none h-7 text-xs w-[140px]" data-testid={`bucket-edit-input-${r.id}`} />
            <Button onClick={save} disabled={busy} size="sm" className="rounded-none h-7 px-2 text-[10px] uppercase tracking-widest font-bold bg-[#002FA7] hover:bg-[#002480]" data-testid={`bucket-save-${r.id}`}>{busy ? "…" : "Save"}</Button>
            <Button onClick={() => { setEditing(false); setVal(r.expected_collection_date ? r.expected_collection_date.slice(0, 10) : ""); }} variant="ghost" size="sm" className="rounded-none h-7 px-2 text-[10px] uppercase tracking-widest font-bold">✕</Button>
          </div>
        ) : (
          <>
            {r.expected_collection_date ? fmtDate(r.expected_collection_date) : <span className="text-[#9CA3AF]">No date</span>}
          </>
        )}
      </td>
      <td className="px-4 py-2 text-xs">
        {r.project_id ? <Link to={`/projects/${r.project_id}`} className="font-bold hover:underline">{r.customer_name || "—"}</Link> : "—"}
        {r.is_synthetic && <span className="ml-2 text-[9px] uppercase tracking-widest font-bold bg-[#FEF3C7] text-[#92400E] px-1.5 py-0.5">Auto</span>}
      </td>
      <td className="px-4 py-2 text-xs label-eyebrow !text-[10px]">{r.project_qr || "—"}</td>
      <td className="px-4 py-2 text-xs">{r.milestone || r.note || "—"}</td>
      <td className="px-4 py-2 text-xs">{inr(due)}</td>
      <td className="px-4 py-2 text-xs text-[#065F46]">{receivedSoFar > 0 ? inr(receivedSoFar) : "—"}</td>
      <td className="px-4 py-2 text-xs font-bold text-[#DC2626]">{inr(r.amount_inr)}</td>
      <td className="px-4 py-2 text-xs"><span className={`text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 ${statusCls}`} data-testid={`bucket-status-${r.id}`}>{statusLabel}</span></td>
      <td className="px-4 py-2 text-xs">
        {r.days_overdue !== undefined ? <span className="text-[#DC2626] font-bold">{r.days_overdue}d overdue</span>
         : r.days_until_due !== undefined ? <span className="text-[#4B5563]">in {r.days_until_due}d</span> : "—"}
      </td>
      <td className="px-4 py-2 text-right whitespace-nowrap">
        {!editing && (
          <div className="inline-flex gap-1">
            <Button onClick={() => onReceive(r)} disabled={busy} size="sm" className="rounded-none h-7 px-2 text-[10px] uppercase tracking-widest font-bold bg-[#10B981] hover:bg-[#059669] text-white" data-testid={`bucket-collect-${r.id}`}>
              ✓ Mark Received
            </Button>
            <Button onClick={() => setEditing(true)} disabled={busy} size="sm" variant="ghost" className="rounded-none h-7 px-2 text-[10px] uppercase tracking-widest font-bold text-[#002FA7]" data-testid={`bucket-edit-${r.id}`}>
              {r.is_synthetic ? "Set Date" : "Edit Date"}
            </Button>
          </div>
        )}
      </td>
    </tr>
  );
}

function BucketsTab({ params }) {
  const [data, setData] = useState({ buckets: {}, totals: {} });
  const [receivingRow, setReceivingRow] = useState(null);
  const reload = () => api.get("/finance/collection-buckets", { params }).then((r) => setData(r.data));
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [JSON.stringify(params)]);
  useEffect(() => { const h = () => reload(); window.addEventListener("finance:refresh", h); return () => window.removeEventListener("finance:refresh", h); });

  const BUCKETS = [
    { k: "overdue",    label: "Overdue",        tone: "bg-[#FECACA] text-[#991B1B] border-[#FCA5A5]" },
    { k: "this_week",  label: "Due This Week",  tone: "bg-[#FEF3C7] text-[#92400E] border-[#FDE68A]" },
    { k: "this_month", label: "Due This Month", tone: "bg-[#DBEAFE] text-[#1E40AF] border-[#BFDBFE]" },
    { k: "later",      label: "Later",          tone: "bg-[#F3F4F6] text-[#0a0a0a] border-[#E5E7EB]" },
    { k: "no_date",    label: "No Expected Date", tone: "bg-[#F3F4F6] text-[#6B7280] border-[#E5E7EB]" },
  ];
  return (
    <div className="space-y-4" data-testid="buckets-tab">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {BUCKETS.map(({ k, label }) => (
          <StatCard
            key={k}
            testid={`bucket-${k}`}
            label={label}
            value={`${data.totals?.[k]?.count ?? 0}`}
            sub={inr(data.totals?.[k]?.amount || 0)}
            accent={k === "overdue" ? "error" : k === "this_week" ? "warning" : "primary"}
          />
        ))}
      </div>

      {BUCKETS.map(({ k, label, tone }) => {
        const rows = data.buckets?.[k] || [];
        if (rows.length === 0) return null;
        return (
          <div key={k} className="border border-[#E5E7EB] bg-white" data-testid={`bucket-section-${k}`}>
            <div className={`px-4 py-2 border-b uppercase tracking-widest font-bold text-[11px] ${tone}`}>
              {label} · {rows.length} · {inr(data.totals?.[k]?.amount || 0)}
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" style={{ minWidth: 900 }}>
                <thead className="bg-[#F9FAFB] border-b border-[#E5E7EB]">
                  <tr>{["Expected Date", "Customer", "Project ID", "Stage / Note", "Total Due", "Received", "Outstanding", "Status", "Aging", ""].map((h) => (
                    <th key={h} className="text-left label-eyebrow !text-[10px] px-4 py-2">{h}</th>
                  ))}</tr>
                </thead>
                <tbody>
                  {rows.map((r) => <BucketRow key={r.id} r={r} onChanged={reload} onReceive={setReceivingRow} />)}
                </tbody>
              </table>
            </div>
          </div>
        );
      })}
      {Object.values(data.buckets || {}).every((arr) => (arr || []).length === 0) && (
        <div className="border border-[#E5E7EB] bg-white p-12 text-center text-sm text-[#9CA3AF]" data-testid="buckets-empty">
          No pending customer payments. Pending payments appear here once you record them with an Expected Collection Date.
        </div>
      )}

      <ReceiveCollectionDialog
        row={receivingRow}
        onClose={() => setReceivingRow(null)}
        onDone={() => { setReceivingRow(null); reload(); }}
      />
    </div>
  );
}

// --- Export menu ---------------------------------------------------------
function ExportMenu({ params, kind }) {
  const [open, setOpen] = useState(false);
  const doExport = (fmt) => {
    const url = new URL(`/api/finance/export`, window.location.origin);
    url.searchParams.set("fmt", fmt);
    Object.entries(params).forEach(([k, v]) => v != null && v !== "" && url.searchParams.set(k, v));
    if (kind === "collections") { url.searchParams.set("transaction_type", "Project"); url.searchParams.set("kind", "Customer"); }
    if (kind === "expenses")    { url.searchParams.set("kind", "Vendor"); }
    const a = document.createElement("a");
    // axios baseURL points at the API; for downloads we go through the proxied origin
    a.href = url.pathname + url.search;
    a.click();
    setOpen(false);
  };
  return (
    <div className="relative">
      <Button onClick={() => setOpen(!open)} variant="outline" className="rounded-none border-[#0a0a0a] uppercase tracking-widest text-xs font-bold h-11" data-testid="finance-export-btn">
        <Download className="w-4 h-4 mr-2" /> Export
      </Button>
      {open && (
        <div className="absolute right-0 top-12 z-30 border border-[#0a0a0a] bg-white shadow-lg min-w-[160px]" data-testid="finance-export-menu">
          <button onClick={() => doExport("csv")} className="w-full text-left px-4 py-2.5 text-xs uppercase tracking-widest font-bold hover:bg-[#F9FAFB] flex items-center gap-2" data-testid="export-csv"><FileSpreadsheet className="w-4 h-4" /> CSV</button>
          <button onClick={() => doExport("xlsx")} className="w-full text-left px-4 py-2.5 text-xs uppercase tracking-widest font-bold hover:bg-[#F9FAFB] flex items-center gap-2" data-testid="export-xlsx"><FileSpreadsheet className="w-4 h-4 text-[#10B981]" /> Excel</button>
          <button onClick={() => doExport("pdf")} className="w-full text-left px-4 py-2.5 text-xs uppercase tracking-widest font-bold hover:bg-[#F9FAFB] flex items-center gap-2" data-testid="export-pdf"><FileIcon className="w-4 h-4 text-[#DC2626]" /> PDF</button>
        </div>
      )}
    </div>
  );
}

// ------------------------------------------------------------------ Transaction Dialog
function PaymentDialog({ onDone }) {
  const [nonProjCategories, setNonProjCategories] = useState([]);
  const [projTaxonomy, setProjTaxonomy] = useState({});
  const [f, setF] = useState({
    transaction_type: "Project", project_id: "", kind: "Customer",
    milestone: "Token Payment", expense_type: "", category: "",
    amount_inr: 0, method: "UPI", reference: "", note: "", status: "Received",
    tx_date: new Date().toISOString().slice(0, 10),
    expected_collection_date: "",
  });
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get("/finance/non-project-categories").then((r) => setNonProjCategories(r.data.categories || []));
    api.get("/finance/project-expense-categories").then((r) => setProjTaxonomy(r.data.taxonomy || {}));
  }, []);
  const set = (k) => (v) => setF({ ...f, [k]: v?.target ? v.target.value : v });

  const isNonProject = f.transaction_type === "Non-Project Expense";
  const isCustomer = !isNonProject && f.kind === "Customer";
  const isProjectExpense = !isNonProject && f.kind === "Vendor";
  const expenseTypes = Object.keys(projTaxonomy);
  const categoriesForType = projTaxonomy[f.expense_type] || [];

  const submit = async () => {
    if (busy) return; // Guard against double-click / rapid re-submit
    if (!isNonProject && !f.project_id) { toast.error("Pick a project"); return; }
    if (isProjectExpense && !f.expense_type) { toast.error("Pick an expense type"); return; }
    if (isProjectExpense && !f.category) { toast.error("Pick an expense category"); return; }
    setBusy(true);
    const payload = { ...f, amount_inr: parseFloat(f.amount_inr) };
    if (!payload.category) delete payload.category;
    if (!payload.expense_type) delete payload.expense_type;
    if (!payload.reference) delete payload.reference;
    if (!payload.expected_collection_date) delete payload.expected_collection_date;
    try {
      await api.post("/payments", payload);
      toast.success("Transaction recorded");
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
      setBusy(false); // release on error only — onDone unmounts dialog on success
    }
  };

  return (
    <DialogContent className="rounded-none border-[#0a0a0a] max-w-xl max-h-[88vh] overflow-y-auto p-5" data-testid="payment-dialog">
      <DialogHeader className="pb-1"><DialogTitle className="font-black text-xl tracking-tight">Record Transaction</DialogTitle></DialogHeader>
      <div className="space-y-2.5">
        {/* Row 1: type + project (or non-project) */}
        <div className="grid grid-cols-2 gap-2">
          <div>
            <Label className="label-eyebrow">Transaction Type</Label>
            <Select value={f.transaction_type} onValueChange={(v) => setF({
              ...f, transaction_type: v,
              project_id: v === "Non-Project Expense" ? "" : f.project_id,
              kind: v === "Non-Project Expense" ? "Vendor" : f.kind,
              category: v === "Non-Project Expense" ? "Office Expense" : "",
              expense_type: v === "Non-Project Expense" ? "" : f.expense_type,
              milestone: v === "Non-Project Expense" ? "" : (f.milestone || "Token Payment"),
            })}>
              <SelectTrigger className="rounded-none mt-1 h-9" data-testid="payment-txtype"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="Project">Project Related</SelectItem>
                <SelectItem value="Non-Project Expense">Non-Project Expense</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {!isNonProject && (
            <div>
              <Label className="label-eyebrow">Project</Label>
              <SearchablePicker
                items={[]}
                value={f.project_id}
                onChange={(id) => setF({ ...f, project_id: id })}
                optionsEndpoint="/projects/options"
                renderRow={renderProjectRow}
                selectedLabel={(p) => `${p.customer_name} — ${p.capacity_kw}kW · ${p.qr_code}`}
                placeholder="Search by customer, QR or location…"
                testid="payment-project"
                emptyHint="No projects match."
              />
            </div>
          )}
        </div>

        {/* Row 2: kind (only for project txns) */}
        {!isNonProject && (
          <div>
            <Label className="label-eyebrow">Kind</Label>
            <Select value={f.kind} onValueChange={(v) => setF({ ...f, kind: v, expense_type: "", category: "" })}>
              <SelectTrigger className="rounded-none mt-1 h-9" data-testid="payment-kind"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="Customer">Customer Collection</SelectItem>
                <SelectItem value="Vendor">Project Expense</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}

        {/* Customer payment stage */}
        {isCustomer && (
          <div>
            <Label className="label-eyebrow">Payment Stage</Label>
            <Select value={f.milestone} onValueChange={(v) => setF({ ...f, milestone: v })}>
              <SelectTrigger className="rounded-none mt-1 h-9" data-testid="payment-milestone">
                <SelectValue placeholder="Pick payment stage" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Token Payment">Token Payment</SelectItem>
                <SelectItem value="Advance Payment">Advance Payment</SelectItem>
                <SelectItem value="2nd Installment">2nd Installment</SelectItem>
                <SelectItem value="Other Amount">Other Amount</SelectItem>
                <SelectItem value="Final Settlement">Final Settlement</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}

        {/* Project expense: cascading Expense Type → Category */}
        {isProjectExpense && (
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="label-eyebrow">Expense Type</Label>
              <Select value={f.expense_type} onValueChange={(v) => setF({ ...f, expense_type: v, category: "" })}>
                <SelectTrigger className="rounded-none mt-1 h-9" data-testid="payment-exptype"><SelectValue placeholder="Pick type" /></SelectTrigger>
                <SelectContent className="max-h-72">{expenseTypes.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="label-eyebrow">Category</Label>
              <Select value={f.category} onValueChange={set("category")} disabled={!f.expense_type}>
                <SelectTrigger className="rounded-none mt-1 h-9" data-testid="payment-category"><SelectValue placeholder={f.expense_type ? "Pick category" : "Pick type first"} /></SelectTrigger>
                <SelectContent className="max-h-72">{categoriesForType.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
        )}
        {isProjectExpense && (
          <div>
            <Label className="label-eyebrow">Expense Head (optional)</Label>
            <Input value={f.milestone} onChange={set("milestone")} placeholder="Free-form note / vendor name" className="rounded-none mt-1 h-9" data-testid="payment-head" />
          </div>
        )}

        {/* Non-project category */}
        {isNonProject && (
          <div>
            <Label className="label-eyebrow">Expense Category</Label>
            <Select value={f.category || "Office Expense"} onValueChange={set("category")}>
              <SelectTrigger className="rounded-none mt-1 h-9" data-testid="payment-nonproj-category"><SelectValue /></SelectTrigger>
              <SelectContent className="max-h-72">{nonProjCategories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        )}

        {/* Amount + Date */}
        <div className="grid grid-cols-2 gap-2">
          <div>
            <Label className="label-eyebrow">Amount (₹)</Label>
            <Input type="number" value={f.amount_inr} onChange={set("amount_inr")} className="rounded-none mt-1 h-9" data-testid="payment-amount" />
          </div>
          <div>
            <Label className="label-eyebrow">Tx Date</Label>
            <Input type="date" value={f.tx_date} onChange={set("tx_date")} className="rounded-none mt-1 h-9" data-testid="payment-date" />
          </div>
        </div>

        {/* Method + Ref */}
        <div className="grid grid-cols-2 gap-2">
          <div>
            <Label className="label-eyebrow">Method</Label>
            <Input value={f.method} onChange={set("method")} className="rounded-none mt-1 h-9" placeholder="UPI / NEFT / Cash" />
          </div>
          <div>
            <Label className="label-eyebrow">Reference / UTR</Label>
            <Input value={f.reference} onChange={set("reference")} className="rounded-none mt-1 h-9" />
          </div>
        </div>

        <div>
          <Label className="label-eyebrow">Note / Remarks</Label>
          <Input value={f.note} onChange={set("note")} className="rounded-none mt-1 h-9" />
        </div>

        {!isNonProject && (
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="label-eyebrow">Status</Label>
              <Select value={f.status} onValueChange={set("status")}>
                <SelectTrigger className="rounded-none mt-1 h-9"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="Received">Received</SelectItem><SelectItem value="Pending">Pending</SelectItem></SelectContent>
              </Select>
            </div>
            {isCustomer && f.status === "Pending" && (
              <div>
                <Label className="label-eyebrow">Expected Collection Date</Label>
                <Input type="date" value={f.expected_collection_date} onChange={set("expected_collection_date")} className="rounded-none mt-1 h-9" data-testid="payment-expected-date" />
              </div>
            )}
          </div>
        )}

        <Button onClick={submit} disabled={busy} className="w-full rounded-none bg-[#002FA7] hover:bg-[#002480] h-10 uppercase tracking-widest text-xs font-bold mt-2 disabled:opacity-60" data-testid="payment-submit">{busy ? "Saving…" : "Save Transaction"}</Button>
      </div>
    </DialogContent>
  );
}

// ------------------------------------------------------------------ Audit Dialog
function AuditDialog({ open, onOpenChange }) {
  const [rows, setRows] = useState([]);
  useEffect(() => { if (open) api.get("/finance/audit").then((r) => setRows(r.data || [])); }, [open]);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-none border-[#0a0a0a] max-w-4xl max-h-[85vh] overflow-y-auto" data-testid="finance-audit-dialog">
        <DialogHeader><DialogTitle className="font-black text-2xl tracking-tight">Finance Audit Trail</DialogTitle></DialogHeader>
        <div className="space-y-2 pt-2">
          {rows.length === 0 ? (
            <div className="text-sm text-[#9CA3AF] p-8 text-center">No audit entries yet.</div>
          ) : rows.map((r) => (
            <div key={r.id} className="border border-[#E5E7EB] p-3 text-xs" data-testid={`audit-row-${r.id}`}>
              <div className="flex justify-between items-baseline">
                <div>
                  <span className={`uppercase tracking-widest font-bold text-[10px] px-2 py-0.5 mr-2 ${r.action === "created" ? "bg-[#D1FAE5] text-[#065F46]" : r.action === "updated" ? "bg-[#DBEAFE] text-[#1E40AF]" : "bg-[#FECACA] text-[#991B1B]"}`}>{r.action}</span>
                  <span className="font-bold">{r.by_name}</span> <span className="text-[#9CA3AF]">({r.by_role})</span>
                </div>
                <span className="text-[#9CA3AF]">{fmtDate(r.at)} {(r.at || "").slice(11, 16)}</span>
              </div>
              <div className="mt-1 text-[#4B5563]">
                {r.transaction_type} · {r.kind} · <b>{inr(r.amount_inr || 0)}</b> · status: {r.status} · payment_id: <span className="font-mono">{(r.payment_id || "").slice(0, 8)}</span>
              </div>
              {r.before && r.action === "updated" && (
                <details className="mt-1 text-[10px] text-[#4B5563]"><summary className="cursor-pointer">Show diff</summary>
                  <pre className="bg-[#F9FAFB] p-2 mt-1 overflow-x-auto">{JSON.stringify({ before: r.before, after: r.after }, null, 2)}</pre>
                </details>
              )}
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
